import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ItineraryActual {
  id: string;
  itinerary_item_id: string;
  lead_id: string;
  amount: number;
  currency: string;
  paid_on: string;
  supplier_name: string | null;
  reference: string | null;
  notes: string | null;
  attachment_url: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export function useItineraryActuals(leadId: string) {
  return useQuery({
    queryKey: ["itinerary-actuals", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_item_actuals" as any)
        .select("*")
        .eq("lead_id", leadId)
        .order("paid_on", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as ItineraryActual[];
    },
    enabled: !!leadId,
  });
}

export function useCreateActual(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<ItineraryActual>) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("itinerary_item_actuals" as any)
        .insert({ ...payload, lead_id: leadId, created_by: userData.user?.id } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["itinerary-actuals", leadId] });
      toast.success("Payment logged");
    },
    onError: (e: any) => toast.error(e.message || "Failed to log payment"),
  });
}

export function useUpdateActual(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<ItineraryActual> & { id: string }) => {
      const { data, error } = await supabase
        .from("itinerary_item_actuals" as any)
        .update(patch as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["itinerary-actuals", leadId] });
      toast.success("Payment updated");
    },
    onError: (e: any) => toast.error(e.message || "Failed to update payment"),
  });
}

export function useDeleteActual(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("itinerary_item_actuals" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["itinerary-actuals", leadId] });
      toast.success("Payment removed");
    },
    onError: (e: any) => toast.error(e.message || "Failed to remove payment"),
  });
}